import SEOHead from '@/components/SEOHead';
import { NeobrutalButton } from '@/components/marketing/v2/NeobrutalButton';
import { FinalCtaSection } from '@/components/marketing/v2/FinalCtaSection';
import { SOFTWARE_APP_JSONLD } from '@/lib/seo';

const PATH = '/velko-vs-cahier-de-caisse';

// Lignes du tableau comparatif. `paper` = cahier papier, `velko` = avec Velko POS.
const ROWS: { label: string; paper: string; velko: string }[] = [
  {
    label: 'Suivi des ventes',
    paper: 'Écrit à la main, illisible le soir, sujet aux oublis',
    velko: 'Chaque vente enregistrée, total du jour calculé tout seul',
  },
  {
    label: 'Stock',
    paper: 'Tu découvres la rupture quand le client la voit',
    velko: 'Stock à jour à chaque vente, alerte avant la rupture',
  },
  {
    label: 'Sans internet',
    paper: 'Fonctionne, mais rien n’est exploitable ensuite',
    velko: 'Vend hors ligne, puis synchronise au retour du réseau',
  },
  {
    label: 'Mobile Money',
    paper: 'Noté à part, jamais réconcilié',
    velko: 'Orange Money, MTN MoMo et Wave sur le même ticket',
  },
  {
    label: 'Plusieurs boutiques',
    paper: 'Un cahier par point de vente, aucun total commun',
    velko: 'Toutes les boutiques dans un seul tableau de bord',
  },
  {
    label: 'Vol et pertes',
    paper: 'Invisibles : l’argent manque sans explication',
    velko: 'Journal d’audit + écarts détectés (coulage, surdosage)',
  },
  {
    label: 'Rapports',
    paper: 'À recalculer à la main, source d’erreurs',
    velko: 'Chiffre d’affaires, marge et best-sellers en 1 clic',
  },
  {
    label: 'Coût',
    paper: 'Gratuit… mais te coûte en erreurs et en temps',
    velko: 'Plan gratuit, offres dès 5 000 F CFA/mois',
  },
];

const FAQ: { q: string; a: string }[] = [
  {
    q: 'Velko remplace-t-il vraiment le cahier de caisse ?',
    a: 'Oui. Velko enregistre chaque vente, suit le stock et calcule tes totaux automatiquement. Tu gardes la même habitude — encaisser — mais sans recopier ni recalculer à la main, et sans perdre l’information le lendemain.',
  },
  {
    q: 'Faut-il une connexion internet pour utiliser Velko ?',
    a: 'Non. Velko est conçu hors ligne d’abord : la caisse continue de fonctionner sans réseau, puis tout se synchronise automatiquement dès que la connexion revient. C’est le principal avantage face au cahier dans les zones mal couvertes.',
  },
  {
    q: 'Combien coûte Velko par rapport au cahier ?',
    a: 'Le cahier est gratuit mais coûte cher en erreurs, en oublis et en temps perdu chaque soir. Velko a un plan gratuit pour démarrer ; les offres payantes commencent à 5 000 F CFA/mois, avec 14 jours d’essai sans carte bancaire.',
  },
  {
    q: 'Mes données sont-elles en sécurité ?',
    a: 'Oui. La base locale est chiffrée (SQLCipher), l’accès se fait par code PIN et chaque action est tracée dans un journal d’audit. Contrairement à un cahier qui peut être perdu, brûlé ou falsifié, tes données restent intactes et sauvegardées.',
  },
];

export default function CompareCahier() {
  const jsonLd: Record<string, unknown>[] = [
    SOFTWARE_APP_JSONLD,
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    },
  ];

  return (
    <>
      <SEOHead
        title="Velko vs cahier de caisse : pourquoi passer au logiciel de caisse"
        description="Cahier de caisse ou logiciel de caisse ? Comparatif honnête pour un commerce en Afrique : suivi des ventes, stock, hors ligne, Mobile Money, vol et coût. Quand le cahier suffit, et quand Velko change tout."
        path={PATH}
        ogImage="/brand/og/og-home.png"
        jsonLd={jsonLd}
        breadcrumbs={[
          { name: 'Accueil', path: '/' },
          { name: 'Velko vs cahier de caisse', path: PATH },
        ]}
      />

      {/* Hero */}
      <section className="bg-paper py-20 md:py-28">
        <div className="mx-auto max-w-4xl px-6 text-center md:px-8">
          <div className="inline-flex items-center gap-2 border-2 border-charcoal-800 bg-lime-500 px-3 py-1 font-marketing-mono text-[11px] font-bold uppercase tracking-[0.14em] text-charcoal-800 shadow-neobrutal-2">
            Comparatif
          </div>
          <h1 className="mt-6 font-marketing-display text-[clamp(36px,5.5vw,64px)] font-bold leading-[1.04] tracking-[-0.04em] text-charcoal-800">
            Le cahier de caisse vs <span className="text-lime-600">Velko</span>
          </h1>
          <p className="mx-auto mt-5 max-w-2xl font-marketing-body text-lg leading-relaxed text-charcoal-600">
            Le cahier a tenu ta boutique pendant des années. Mais il ne te dira jamais combien tu
            as réellement gagné aujourd’hui, ni où part l’argent. Voici, ligne par ligne, ce qui
            change quand tu passes au logiciel de caisse.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <NeobrutalButton as="link" to="/register" variant="primary" size="lg">
              Essayer gratuitement
            </NeobrutalButton>
            <NeobrutalButton as="link" to="/fonctionnalites" variant="secondary" size="lg">
              Voir les fonctionnalités
            </NeobrutalButton>
          </div>
        </div>
      </section>

      {/* Tableau comparatif */}
      <section className="bg-paper pb-8">
        <div className="mx-auto max-w-4xl px-6 md:px-8">
          <div className="overflow-hidden border-2 border-charcoal-800 bg-white shadow-neobrutal-6">
            <div className="grid grid-cols-[1.1fr_1fr_1fr] border-b-2 border-charcoal-800 bg-charcoal-800 text-white">
              <div className="px-4 py-3 font-marketing-mono text-[11px] font-bold uppercase tracking-[0.12em] md:px-6">
                Critère
              </div>
              <div className="border-l-2 border-white/20 px-4 py-3 font-marketing-display text-[13px] font-bold md:px-6">
                Cahier papier
              </div>
              <div className="border-l-2 border-white/20 bg-lime-500 px-4 py-3 font-marketing-display text-[13px] font-bold text-charcoal-800 md:px-6">
                Avec Velko
              </div>
            </div>
            {ROWS.map((row, i) => (
              <div
                key={row.label}
                className={`grid grid-cols-[1.1fr_1fr_1fr] ${
                  i < ROWS.length - 1 ? 'border-b border-charcoal-200' : ''
                }`}
              >
                <div className="px-4 py-4 font-marketing-display text-[13.5px] font-bold text-charcoal-800 md:px-6">
                  {row.label}
                </div>
                <div className="border-l-2 border-charcoal-200 px-4 py-4 font-marketing-body text-[13.5px] leading-snug text-charcoal-600 md:px-6">
                  {row.paper}
                </div>
                <div className="border-l-2 border-charcoal-200 bg-lime-50 px-4 py-4 font-marketing-body text-[13.5px] font-medium leading-snug text-charcoal-800 md:px-6">
                  {row.velko}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contenu éditorial (SEO + GEO) */}
      <section className="bg-paper py-14 md:py-20">
        <div className="mx-auto max-w-3xl space-y-10 px-6 md:px-8">
          <div>
            <h2 className="font-marketing-display text-[clamp(24px,3.5vw,34px)] font-bold tracking-[-0.03em] text-charcoal-800">
              Quand le cahier de caisse suffit (soyons honnêtes)
            </h2>
            <p className="mt-4 font-marketing-body text-[16px] leading-relaxed text-charcoal-600">
              Si tu vends quelques articles par jour, que tu es seul derrière le comptoir et que tu
              ne gères pas de stock, un cahier bien tenu fait le travail. Pas besoin d’un logiciel
              pour compter trois ventes. L’honnêteté d’abord : un outil n’a de valeur que s’il te
              fait gagner du temps ou de l’argent.
            </p>
          </div>

          <div>
            <h2 className="font-marketing-display text-[clamp(24px,3.5vw,34px)] font-bold tracking-[-0.03em] text-charcoal-800">
              Quand le cahier te coûte de l’argent sans que tu le voies
            </h2>
            <p className="mt-4 font-marketing-body text-[16px] leading-relaxed text-charcoal-600">
              Dès que tu emploies un vendeur, que tu gères du stock, ou que tu ouvres une deuxième
              boutique, le cahier devient un angle mort. Tu ne sais plus si la caisse du soir est
              juste, quel produit part le plus, ni pourquoi il manque de la marchandise. Le coulage,
              les ventes non notées et les erreurs de calcul grignotent ta marge — silencieusement.
            </p>
            <p className="mt-4 font-marketing-body text-[16px] leading-relaxed text-charcoal-600">
              Velko remplace le cahier par une caisse qui enregistre chaque vente, suit le stock en
              temps réel et te montre ton vrai chiffre d’affaires. Le tout <strong>hors ligne</strong>{' '}
              — la caisse fonctionne même sans réseau, puis synchronise automatiquement au retour de
              la connexion — et avec le <strong>Mobile Money</strong> (Orange Money, MTN MoMo, Wave)
              intégré au ticket.
            </p>
          </div>

          <div>
            <h2 className="font-marketing-display text-[clamp(24px,3.5vw,34px)] font-bold tracking-[-0.03em] text-charcoal-800">
              Passer du cahier à Velko, sans douleur
            </h2>
            <p className="mt-4 font-marketing-body text-[16px] leading-relaxed text-charcoal-600">
              Pas besoin de tout informatiser d’un coup. Tu crées ton compte gratuitement, tu
              ajoutes tes produits, et tu encaisses ta première vente en moins de trois minutes. Le
              geste reste le même qu’avec le cahier — encaisser — mais cette fois, le total se
              calcule seul, le stock se met à jour, et le rapport du jour est prêt sans un seul
              calcul de ta part.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-paper pb-16 md:pb-24">
        <div className="mx-auto max-w-3xl px-6 md:px-8">
          <h2 className="font-marketing-display text-[clamp(24px,3.5vw,34px)] font-bold tracking-[-0.03em] text-charcoal-800">
            Les questions qu’on nous pose
          </h2>
          <div className="mt-8 space-y-4">
            {FAQ.map((item) => (
              <div
                key={item.q}
                className="border-2 border-charcoal-800 bg-white p-5 shadow-neobrutal-2 md:p-6"
              >
                <h3 className="font-marketing-display text-[17px] font-bold text-charcoal-800">
                  {item.q}
                </h3>
                <p className="mt-2 font-marketing-body text-[15px] leading-relaxed text-charcoal-600">
                  {item.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FinalCtaSection
        eyebrow="Quitte le cahier"
        title="Ta caisse mérite mieux"
        highlight="qu’un stylo."
        subtitle="Garde tes habitudes, gagne le calcul automatique, le stock à jour et le hors ligne. Sans carte bancaire, prêt en 3 minutes."
        primaryLabel="Créer mon compte gratuit"
        primaryTo="/register"
        secondaryLabel="Voir les tarifs"
        secondaryTo="/tarifs"
        guarantees={['Plan gratuit', 'Sans CB', 'Hors ligne natif', 'Support FR']}
      />
    </>
  );
}
